// src/components/workspace/WorkspaceTable.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Skeleton,
  TablePagination,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
 
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import BusinessIcon from "@mui/icons-material/Business";
import { Workspace } from "../../types/models";

type Order = "asc" | "desc";
const DEBOUNCE_MS = 250;
const PREFS_KEY = "workspaceTablePrefs.v1";

interface Props {
  workspaces: Workspace[];
  selectedWorkspace: Workspace | null;
  setSelectedWorkspace: (workspace: Workspace) => void;
  fetchDepartments: (id: number) => void;
  showSnackbar: (msg: string, severity: "success" | "error") => void;
  loading: boolean;
  setDeleteTarget: (item: { id: number; type: "workspace" }) => void;
  setDeleteDialogOpen: (open: boolean) => void;
  setCreateDialogOpen: () => void;
  setEditingItem: (item: { id: number; name: string; type: "workspace" }) => void;
}

function useDebouncedValue<T>(value: T, delay = DEBOUNCE_MS) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function hashHue(str: string) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h << 5) - h + str.charCodeAt(i);
  return Math.abs(h) % 360;
}

const WorkspaceTable: React.FC<Props> = ({
  workspaces,
  selectedWorkspace,
  setSelectedWorkspace,
  setCreateDialogOpen,
  setDeleteTarget,
  setDeleteDialogOpen,
  setEditingItem,
  fetchDepartments,
  showSnackbar,
  loading,
}) => {
  const theme = useTheme();

  // ----- persisted prefs -----
  const [searchInput, setSearchInput] = useState("");
  const [order, setOrder] = useState<Order>("asc");
  const [compact, setCompact] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      if (!raw) return;
      const prefs = JSON.parse(raw || "{}");
      if (typeof prefs.search === "string") setSearchInput(prefs.search);
      if (prefs.order === "asc" || prefs.order === "desc") setOrder(prefs.order);
      if (typeof prefs.compact === "boolean") setCompact(prefs.compact);
      if (typeof prefs.rowsPerPage === "number") setRowsPerPage(prefs.rowsPerPage);
    } catch {}
  }, []);

  useEffect(() => {
    const payload = {
      search: searchInput,
      order,
      compact,
      rowsPerPage,
    };
    localStorage.setItem(PREFS_KEY, JSON.stringify(payload));
  }, [searchInput, order, compact, rowsPerPage]);

  // ----- search -----
  const debouncedQuery = useDebouncedValue(searchInput.trim().toLowerCase());
  const searchRef = useRef<HTMLInputElement>(null);

  // ----- sorting -----
  const handleRequestSort = () => setOrder((p) => (p === "asc" ? "desc" : "asc"));

  // ----- pagination -----
  const [page, setPage] = useState(0);
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ----- keyboard shortcuts -----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = (e.key || "").toLowerCase();
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const focusPressed =
        (isMac && e.metaKey && key === "k") || (!isMac && e.ctrlKey && key === "k");
      const createPressed = key === "n" && !e.metaKey && !e.ctrlKey && !e.altKey;
      const delPressed = key === "delete" || key === "backspace";
      const enterPressed = key === "enter";
      const up = key === "arrowup";
      const down = key === "arrowdown";

      if (focusPressed) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (createPressed) {
        e.preventDefault();
        setCreateDialogOpen();
      } else if (delPressed && selectedWorkspace) {
        e.preventDefault();
        setDeleteTarget({ id: selectedWorkspace.id, type: "workspace" });
        setDeleteDialogOpen(true);
      } else if (enterPressed && selectedWorkspace) {
        e.preventDefault();
        setEditingItem({
          id: selectedWorkspace.id,
          name: selectedWorkspace.name,
          type: "workspace",
        });
      } else if ((up || down) && sorted.length) {
        e.preventDefault();
        const currentIndex = paged.findIndex((w) => w.id === selectedWorkspace?.id);
        let nextIndex = 0;
        if (currentIndex === -1) nextIndex = 0;
        else nextIndex = Math.max(0, Math.min(paged.length - 1, currentIndex + (down ? 1 : -1)));
        const next = paged[nextIndex];
        if (next) {
          setSelectedWorkspace(next);
          fetchDepartments(next.id);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    setCreateDialogOpen,
    selectedWorkspace,
    setDeleteTarget,
    setDeleteDialogOpen,
    setEditingItem,
    setSelectedWorkspace,
    fetchDepartments,
  ]);

  // ----- filter + sort + paginate -----
  const filtered = useMemo(() => {
    if (!debouncedQuery) return workspaces;
    return workspaces.filter((w) => w.name.toLowerCase().includes(debouncedQuery));
  }, [workspaces, debouncedQuery]);

  const sorted = useMemo(() => {
    const arr = filtered.slice();
    arr.sort((a, b) => {
      const an = a.name.toLowerCase();
      const bn = b.name.toLowerCase();
      if (an < bn) return order === "asc" ? -1 : 1;
      if (an > bn) return order === "asc" ? 1 : -1;
      return a.id - b.id; // stable
    });
    return arr;
  }, [filtered, order]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  useEffect(() => setPage(0), [debouncedQuery]);

  // ----- helpers -----
  const clearSearch = () => setSearchInput("");

  const onRowClick = (workspace: Workspace) => {
    setSelectedWorkspace(workspace);
    fetchDepartments(workspace.id);
  };

  const onEdit = (workspace: Workspace) => {
    setEditingItem({ id: workspace.id, name: workspace.name, type: "workspace" });
  };

  const onDelete = (workspace: Workspace) => {
    setDeleteTarget({ id: workspace.id, type: "workspace" });
    setDeleteDialogOpen(true);
  };

  const exportCSV = useCallback(() => {
    const header = ["id", "name"];
    const lines = [header.join(",")].concat(
      sorted.map((w) => [w.id, `"${w.name.replace(/"/g, '""')}"`].join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "workspaces.csv";
    a.click();
    URL.revokeObjectURL(url);
    showSnackbar("Exported current results to CSV", "success");
  }, [sorted, showSnackbar]);

  const highlight = (text: string, term: string) => {
    if (!term) return text;
    const idx = text.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + term.length);
    const after = text.slice(idx + term.length);
    return (
      <>
        {before}
        <Box
          component="mark"
          sx={{
            px: 0.3,
            borderRadius: 0.5,
            bgcolor: (t) =>
              t.palette.mode === "dark" ? "warning.dark" : "warning.light",
            color: "inherit",
          }}
        >
          {match}
        </Box>
        {after}
      </>
    );
  };

  // Context menu
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<Workspace | null>(null);
  const openMenu = Boolean(menuAnchor);

  const handleRowContextMenu = (
    e: React.MouseEvent<HTMLTableRowElement>,
    workspace: Workspace
  ) => {
    e.preventDefault();
    setMenuRow(workspace);
    setMenuAnchor(e.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  // ----- UI -----
  const total = workspaces.length;
  const shown = sorted.length;

  return (
    <Box>
      {/* Header Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))"
              : "linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0.02))",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" fontWeight={800}>
              Workspaces
            </Typography>
            <Chip label={`${shown.toLocaleString()} / ${total.toLocaleString()}`} size="small" />
            {selectedWorkspace && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`Selected: ${selectedWorkspace.name}`}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.8 }}>
            <KeyboardIcon fontSize="small" />
            <Typography variant="caption">
              <strong>Ctrl/Cmd + K</strong> to search • <strong>N</strong> to add •{" "}
              <strong>↑/↓</strong> to navigate • <strong>Enter</strong> edit •{" "}
              <strong>Del</strong> delete
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Controls Toolbar */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <TextField
          inputRef={searchRef}
          size="small"
          label="Search workspaces"
          placeholder="Type a name…"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <Tooltip title="Clear">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ minWidth: 280, flex: 2 }}
        />

        <Tooltip title={compact ? "Comfortable density" : "Compact density"}>
          <IconButton
            onClick={() => setCompact((v) => !v)}
            color={compact ? "primary" : "default"}
            size="small"
          >
            {compact ? <ViewStreamIcon /> : <ViewCompactIcon />}
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Export CSV (current results)">
          <span>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={exportCSV}
              disabled={loading || sorted.length === 0}
            >
              Export
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Add Workspace (N)">
          <Button variant="contained" startIcon={<AddIcon />} onClick={setCreateDialogOpen}>
            Add
          </Button>
        </Tooltip>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box p={2}>
            {[...Array(6)].map((_, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="rounded" width="40%" height={28} />
                <Skeleton variant="rounded" width="10%" height={28} />
              </Stack>
            ))}
          </Box>
        ) : (
          <Table size={compact ? "small" : "medium"} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell>
                  <TableSortLabel active direction={order} onClick={handleRequestSort}>
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 160 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                "& .MuiTableRow-root:nth-of-type(odd)": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.02)"
                      : "rgba(0,0,0,0.01)",
                },
              }}
            >
              {paged.map((w) => {
                const selected = selectedWorkspace?.id === w.id;
                const hue = hashHue(w.name);
                const bg =
                  theme.palette.mode === "dark"
                    ? `hsl(${hue} 70% 25%)`
                    : `hsl(${hue} 85% 90%)`;
                const fg =
                  theme.palette.mode === "dark"
                    ? theme.palette.getContrastText(`hsl(${hue} 70% 25%)`)
                    : theme.palette.text.primary;
                return (
                  <TableRow
                    key={w.id}
                    hover
                    selected={selected}
                    onClick={() => onRowClick(w)}
                    onDoubleClick={() => onEdit(w)}
                    onContextMenu={(e) => handleRowContextMenu(e, w)}
                    sx={{
                      cursor: "pointer",
                      "& .row-actions": { opacity: 0, transform: "translateY(-2px)", transition: "all .15s" },
                      "&:hover .row-actions": { opacity: 1, transform: "translateY(0)" },
                    }}
                  >
                    <TableCell>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: bg, color: fg }}>
                        {w.name?.trim()?.[0]?.toUpperCase() || <BusinessIcon fontSize="small" />}
                      </Avatar>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography noWrap title={w.name} fontWeight={selected ? 700 : 500}>
                          {highlight(w.name, debouncedQuery)}
                        </Typography>
                        {selected && <Chip size="small" label="Selected" color="primary" variant="outlined" />}
                      </Stack>
                    </TableCell>

                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                        className="row-actions"
                      >
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(w);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(w);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuRow(w);
                              setMenuAnchor(e.currentTarget);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

              {sorted.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        No workspaces found
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {debouncedQuery
                          ? "Try a different search or clear the filter."
                          : "Get started by creating your first workspace."}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {debouncedQuery && (
                          <Button variant="outlined" onClick={clearSearch} startIcon={<ClearIcon />}>
                            Clear search
                          </Button>
                        )}
                        <Button variant="contained" onClick={setCreateDialogOpen} startIcon={<AddIcon />}>
                          Add workspace
                        </Button>
                      </Stack>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!loading && sorted.length > 0 && (
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 1 }}
        />
      )}

      {/* Row context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={openMenu}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2">{menuRow?.name}</Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuRow) onEdit(menuRow);
            handleMenuClose();
          }}
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) onDelete(menuRow);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WorkspaceTable;
